import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { getApiUrl } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = getApiUrl();

/**
 * Landing page for the claim-link emailed by the "Capture Property Form"
 * submission (see routes/list_property.py `_claim_email_html`). The
 * submitter sets a password here, which the backend then uses to
 * auto-log them in as a property_admin and unlocks every pending
 * property submitted with the same email.
 */
export default function ClaimPropertyPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [checking, setChecking] = useState(true);
  const [claimInfo, setClaimInfo] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = "Claim Your Property Listing";
    if (!token) {
      setError("This claim link is missing its token.");
      setChecking(false);
      return;
    }
    let cancelled = false;
    axios
      .get(`${API}/list-property/claim/${token}`)
      .then((r) => {
        if (!cancelled) setClaimInfo(r.data);
      })
      .catch((e) => {
        if (!cancelled) setError(e.response?.data?.detail || "This claim link is invalid or has expired.");
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(
        `${API}/list-property/claim`,
        { token, password },
        { withCredentials: true }
      );
      toast.success("Account set up — welcome!");
      navigate("/property-admin/dashboard", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to claim your listing");
    } finally {
      setSubmitting(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30" data-testid="claim-property-checking">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30" data-testid="claim-property-page">
      <div className="w-full max-w-md p-8">
        <div className="glass-card p-10 space-y-6">
          {error ? (
            <div className="text-center space-y-4" data-testid="claim-property-error">
              <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto" />
              <h1 className="font-serif text-2xl font-medium">Link no longer valid</h1>
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
          ) : (
            <>
              <div className="text-center space-y-2">
                <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto" />
                <h1 className="font-serif text-2xl font-medium">Claim &quot;{claimInfo?.title}&quot;</h1>
                <p className="text-sm text-muted-foreground">
                  Set a password for <strong>{claimInfo?.email_masked}</strong> to finish
                  registering and publish your listing.
                </p>
              </div>

              <form onSubmit={submit} className="space-y-4" data-testid="claim-property-form">
                <div>
                  <Label>Password</Label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    minLength={6}
                    required
                    data-testid="claim-property-password"
                  />
                </div>
                <div>
                  <Label>Confirm password</Label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    minLength={6}
                    required
                    data-testid="claim-property-confirm-password"
                  />
                </div>
                <Button type="submit" disabled={submitting} className="w-full" data-testid="claim-property-submit-btn">
                  {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Setting up…</> : "Set password & sign in"}
                </Button>
              </form>
            </>
          )}
        </div>

        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground link-underline" data-testid="claim-property-back-home">
            ← Back to SellLocalHomes
          </Link>
        </div>
      </div>
    </div>
  );
}
